import React, { createContext, useState } from 'react';
import { useAsync } from 'react-async-hook';
import PropTypes from 'prop-types';

// Components
import LoadingArea from '@molecules/LoadingArea';
import LoadingOverlay from '@molecules/LoadingOverlay';

// Functionality
import useViewer from '@hooks/useViewer';
import {
    createStripeIntent as createStripeIntentAPI,
    fetchStripeCustomer as fetchStripeCustomerAPI,
    deletePaymentMethod as deletePaymentMethodAPI,
    setPaymentMethodAsDefault as setPaymentMethodAsDefaultAPI,
} from '@general/api/billingAPI';

import { fetchMember as fetchMemberAPI, updateMember as updateMemberAPI } from '@general/api/memberAPI';
import { getResponseData } from '@general/api/shared';

export const BillingContext = createContext();

const BillingProvider = ({ children }) => {
    const { viewer } = useViewer();
    const [member, setMember] = useState(null);
    const [savedCards, setSavedCards] = useState([]);
    const [setupIntentSecret, setSetupIntentSecret] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cardTableInfoMessage, setCardTableInfoMessage] = useState(null);
    const [cardTableErrorMessage, setCardTableErrorMessage] = useState(null);
    const [generalInfoMessage, setGeneralInfoMessage] = useState(null);
    const [generalErrorMessage, setGeneralErrorMessage] = useState(null);
    const [autoRenewErrorMessage, setAutoRenewErrorMessage] = useState(null);
    const [autoRenewInfoMessage, setAutoRenewInfoMessage] = useState(null);

    // -----------------------------
    // Actions
    // -----------------------------

    const createStripeIntent = async memberId => {
        setIsSubmitting(true);

        try {
            const { client_secret: clientSecret } = await getResponseData(() => createStripeIntentAPI(memberId));

            if (!clientSecret) {
                setGeneralErrorMessage('There was an error while retrieving a client secret.');
                return null;
            }

            setSetupIntentSecret(clientSecret);
            return clientSecret;
        } catch (error) {
            setGeneralErrorMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const deletePaymentMethod = async paymentMethodId => {
        setIsSubmitting(true);

        try {
            await getResponseData(() => deletePaymentMethodAPI(paymentMethodId));
            const foundCardIndex = savedCards.findIndex(({ id }) => id === paymentMethodId);
            if (foundCardIndex >= 0) {
                savedCards.splice(foundCardIndex, 1);
            }

            // If the removed card was set as default, set the first of the remaining cards as default
            if (savedCards.length > 0 && !savedCards.find(savedCard => savedCard.default)) {
                await setPaymentMethodAsDefaultAPI(member.id, savedCards[0].id);
                savedCards[0].default = true;
            }

            setSavedCards(savedCards);
        } catch (error) {
            setCardTableErrorMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchStripeCustomer = async (memberIdToFetch, setDefault = false) => {
        setIsSubmitting(true);

        try {
            const stripeCustomer = await getResponseData(() => fetchStripeCustomerAPI({ memberId: memberIdToFetch }));
            const { cards } = stripeCustomer;

            if (cards.length === 1 && setDefault) {
                await setPaymentMethodAsDefaultAPI(memberIdToFetch, cards[0].id);
                cards[0].default = true;
            }

            setSavedCards(cards);
            return stripeCustomer;
        } catch (error) {
            setGeneralErrorMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const setPaymentMethodAsDefault = async (memberId, paymentMethodId) => {
        setIsSubmitting(true);

        try {
            await setPaymentMethodAsDefaultAPI(memberId, paymentMethodId);

            savedCards.forEach(savedCard => {
                savedCard.default = savedCard.id === paymentMethodId;
            });

            setSavedCards(savedCards);
        } catch (error) {
            setCardTableErrorMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const updateMember = async (memberIdToUpdate, memberValues) => {
        setIsSubmitting(true);

        try {
            const response = await updateMemberAPI(memberIdToUpdate, memberValues);
            return response;
        } catch (error) {
            setGeneralErrorMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // -----------------------------
    // Initialize data
    // -----------------------------
    useAsync(async () => {
        if (!viewer) {
            return;
        }

        setIsSubmitting(true);

        const memberResponse = await fetchMemberAPI(viewer.recordId);
        setMember(memberResponse);

        const stripeResponse = await getResponseData(() => fetchStripeCustomerAPI({ memberId: viewer.recordId }));
        setSavedCards(stripeResponse.cards);

        const { client_secret: clientSecret } = await getResponseData(() => createStripeIntentAPI(viewer.recordId));
        setSetupIntentSecret(clientSecret);

        setIsSubmitting(false);
    }, [viewer]);

    // -----------------------------
    // Return context wrapper
    // -----------------------------

    if (!viewer) {
        return <LoadingArea />;
    }

    return (
        <BillingContext.Provider
            value={{
                autoRenewErrorMessage,
                autoRenewInfoMessage,
                cardTableErrorMessage,
                cardTableInfoMessage,
                generalErrorMessage,
                generalInfoMessage,
                isSubmitting,
                member,
                memberId: viewer.recordId,
                savedCards,
                setupIntentSecret,
                createStripeIntent,
                deletePaymentMethod,
                fetchStripeCustomer,
                setAutoRenewErrorMessage,
                setAutoRenewInfoMessage,
                setCardTableErrorMessage,
                setCardTableInfoMessage,
                setGeneralErrorMessage,
                setGeneralInfoMessage,
                setIsSubmitting,
                setMember,
                setPaymentMethodAsDefault,
                updateMember,
            }}
        >
            {isSubmitting && <LoadingOverlay />}
            {children}
        </BillingContext.Provider>
    );
};

export default BillingProvider;
